import {useCallback, useEffect, useRef, useState} from 'react';

interface UseTimeoutProps {
  duration?: number; // Duration in seconds, default 120 (2 minutes)
  onTimeout?: () => void;
  autoStart?: boolean;
}

interface UseTimeoutReturn {
  timeLeft: number;
  isRunning: boolean;
  isFinished: boolean;
  formattedTime: string;
  start: () => void;
  stop: () => void;
  reset: () => void;
  restart: () => void;
}

export const useTimeout = ({
  duration = 120,
  onTimeout,
  autoStart = false,
}: UseTimeoutProps = {}): UseTimeoutReturn => {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [isRunning, setIsRunning] = useState(autoStart);
  const [isFinished, setIsFinished] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const onTimeoutRef = useRef(onTimeout);

  // Keep onTimeout callback ref updated
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  // Format seconds to MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return ` ${String(secs).padStart(2, '0')} : ${String(minutes).padStart(
      2,
      '0',
    )}`;
  };

  // Clear interval helper
  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Start the timer
  const start = useCallback(() => {
    if (timeLeft > 0 && !isFinished) {
      setIsRunning(true);
    }
  }, [timeLeft, isFinished]);

  // Stop the timer
  const stop = useCallback(() => {
    setIsRunning(false);
    clearTimer();
  }, [clearTimer]);

  // Reset the timer to initial duration
  const reset = useCallback(() => {
    clearTimer();
    setTimeLeft(duration);
    setIsRunning(false);
    setIsFinished(false);
  }, [duration, clearTimer]);

  // Restart the timer
  const restart = useCallback(() => {
    clearTimer();
    setTimeLeft(duration);
    setIsRunning(true);
    setIsFinished(false);
  }, [duration, clearTimer]);

  // Main countdown effect
  useEffect(() => {
    if (!isRunning || timeLeft <= 0) return;

    intervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearTimer();
          setIsRunning(false);
          setIsFinished(true);
          onTimeoutRef.current?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearTimer();
  }, [isRunning, timeLeft, clearTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  return {
    timeLeft,
    isRunning,
    isFinished,
    formattedTime: formatTime(timeLeft),
    start,
    stop,
    reset,
    restart,
  };
};
